/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { Dispatch, SetStateAction, SyntheticEvent, useEffect } from "react";

import { useTranslation } from "react-i18next";

import Autocomplete from "@/components/Autocomplete/Autocomplete";
import BannerMessage, {
  BANNER_MESSAGE_STATUSES
} from "@/components/BannerMessage/BannerMessage";
import { inputFieldMarginBottom } from "@/shared";

import { NavigationArrowRightRoundedIcon } from "@dotnet/telematics-design-system";

import {
  MOVE_COMPANY_KEYS,
  MoveCompaniesDropdownOption,
  MoveCompaniesPayload,
  MoveOptionsData
} from "../types";

interface MoveCompanyDialogCotentProps {
  optionsData: MoveOptionsData | null;
  payload: MoveCompaniesPayload;
  setPayload: Dispatch<SetStateAction<MoveCompaniesPayload>>;
  setIsReadyToConfirm: Dispatch<SetStateAction<boolean>>;
}

const MoveCompanyDialogContent = ({
  optionsData,
  payload,
  setPayload,
  setIsReadyToConfirm
}: MoveCompanyDialogCotentProps) => {
  const { t } = useTranslation();

  const { companies } = optionsData || {};
  const { sourceCompany, targetCompany } = payload;

  const checkIfFatherOrSameCompany = (companyId: string) =>
    companyId === targetCompany?.parentCompanyId ||
    companyId === targetCompany?.id;

  const handleCompanyChange = (
    value: MoveCompaniesDropdownOption,
    source: MOVE_COMPANY_KEYS
  ) => {
    if (
      source === MOVE_COMPANY_KEYS.SourceCompany &&
      checkIfFatherOrSameCompany(value.id)
    ) {
      return setPayload({
        [MOVE_COMPANY_KEYS.SourceCompany]: value,
        [MOVE_COMPANY_KEYS.TargetCompany]: null
      });
    }

    setPayload(prevPayload => ({
      ...prevPayload,
      [source]: value
    }));
  };

  useEffect(() => {
    setIsReadyToConfirm(targetCompany !== null);
  }, [payload]);

  const getDescendantCompanyIds = (
    companies: MoveCompaniesDropdownOption[],
    parentId: string
  ): Set<string> => {
    const descendants = new Set<string>();

    const collectChildren = (currentParentId: string) => {
      const children = companies.filter(
        company => company.parentCompanyId === currentParentId
      );

      children.forEach(child => {
        if (!descendants.has(child.id)) {
          descendants.add(child.id);
          collectChildren(child.id);
        }
      });
    };

    collectChildren(parentId);

    return descendants;
  };

  const descendantIds = sourceCompany
    ? getDescendantCompanyIds(companies || [], sourceCompany.id)
    : new Set<string>();

  const targetCompanyOptions =
    companies?.filter(company => {
      if (!sourceCompany) {
        return true;
      }

      return (
        company.id !== sourceCompany.id &&
        company.id !== sourceCompany.parentCompanyId &&
        !descendantIds.has(company.id)
      );
    }) || [];

  const sourceCompanyOptions =
    companies?.filter(company => !company.isRoot) || [];

  const showWarning = sourceCompany?.hasSftp;

  return (
    <>
      <BannerMessage
        data-testid="move-company-info-banner"
        status={BANNER_MESSAGE_STATUSES.Info}
        title={t("Dialog##move company##info banner")}
        externalCustomStyle={inputFieldMarginBottom}
      />

      <div
        css={css({
          display: "flex",
          gap: "8px",
          alignItems: "center"
        })}
      >
        <Autocomplete
          size="medium"
          labelLeft={t("Dialog##move company##source company")}
          fullWidth
          disableClearable
          value={sourceCompany}
          options={sourceCompanyOptions || []}
          getOptionLabel={option => option?.name}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          customStyle={inputFieldMarginBottom}
          onChange={(_: SyntheticEvent, value: MoveCompaniesDropdownOption) =>
            handleCompanyChange(value, MOVE_COMPANY_KEYS.SourceCompany)
          }
          textFieldParams={{
            placeholder: t("Dialog##select")
          }}
        />

        <div>
          <NavigationArrowRightRoundedIcon
            style={{
              paddingTop: "14px",
              width: "16px",
              height: "16px"
            }}
          />
        </div>

        <Autocomplete
          size="medium"
          labelLeft={t("Dialog##move company##target company")}
          fullWidth
          disableClearable
          value={targetCompany}
          options={targetCompanyOptions || []}
          getOptionLabel={option => option?.name}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          customStyle={inputFieldMarginBottom}
          onChange={(_: SyntheticEvent, value: MoveCompaniesDropdownOption) =>
            handleCompanyChange(value, MOVE_COMPANY_KEYS.TargetCompany)
          }
          disabled={!sourceCompany}
          textFieldParams={{
            placeholder: t("Dialog##select")
          }}
        />
      </div>

      {showWarning && (
        <BannerMessage
          data-testid="move-company-sftp-warning-banner"
          status={BANNER_MESSAGE_STATUSES.Warning}
          title={t("Dialog##move company##sftp warning")}
          externalCustomStyle={inputFieldMarginBottom}
        />
      )}
    </>
  );
};

export default MoveCompanyDialogContent;
